import ExcelJS from 'exceljs'
import { KP_VAT_RATE, VatMode, type InvoiceTemplatePayload } from '@crm/shared'

/**
 * Генерация Excel-шаблона счёта для загрузки в 1С:УНФ.
 *
 * Только таблица позиций — без шапки с реквизитами продавца, без заголовка
 * документа и без блока «Итого/НДС» (по согласованию с заказчиком). Набор
 * колонок берётся из shared `ORDER_INVOICE_COLUMNS` (payload.columns),
 * меняется в одном месте.
 *
 * Цена в строках — всегда с НДС: обработка УНФ «Загрузка заказов» читает её как цену с НДС и ставит
 * заказу «НДС в сумме». У счёта «НДС сверху» в конце столбец «НДС» = «22% сверху» — напоминание
 * переключить загруженный заказ на «НДС сверху» (УНФ сама поделит цену на 1,22 и получит цену без
 * НДС из КП до копейки; проверено в тестовой УНФ 15.10.2026, docs/features/kp-vat-mode.md).
 * Обработка этот столбец не читает. Счёт «С НДС» — как раньше, без нового столбца.
 */
export async function generateInvoiceUnf(payload: InvoiceTemplatePayload): Promise<void> {
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('Счёт', {
    pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true },
  })

  const onTop = payload.vatMode === VatMode.ON_TOP
  const vatColumn = { key: 'vat', label: 'НДС', width: 12 }
  const cols = onTop ? [...payload.columns, vatColumn] : payload.columns
  ws.columns = cols.map((c) => ({ width: c.width }))

  const font = (size: number, bold = false): Partial<ExcelJS.Font> => ({
    name: 'Times New Roman',
    size,
    bold,
  })
  const border: Partial<ExcelJS.Borders> = {
    top: { style: 'thin' },
    left: { style: 'thin' },
    bottom: { style: 'thin' },
    right: { style: 'thin' },
  }
  const center: Partial<ExcelJS.Alignment> = {
    horizontal: 'center',
    vertical: 'middle',
    wrapText: true,
  }

  let row = 1

  // ── Заголовок таблицы ──
  const headerRow = ws.getRow(row)
  cols.forEach((c, i) => {
    const cell = headerRow.getCell(i + 1)
    cell.value = c.label
    cell.font = font(9, true)
    cell.alignment = center
    cell.border = border
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E1F2' } }
  })
  headerRow.height = 26
  row++

  // ── Строки позиций ──
  const colToValue: Record<string, (r: InvoiceTemplatePayload['rows'][number]) => unknown> = {
    name: (r) => r.name,
    article: (r) => r.article,
    partNumber: (r) => r.partNumber,
    brand: (r) => r.brand ?? '',
    quantity: (r) => r.quantity,
    unit: (r) => r.unit ?? 'шт',
    price: (r) => r.price,
    // УНФ-классификация (с 13.07.2026): «Категория» + «Входит в группу».
    unfCategory: (r) => r.unfCategory,
    unfGroup: (r) => r.unfGroup,
    vat: () => `${KP_VAT_RATE}% сверху`,
  }
  for (const dataRow of payload.rows) {
    const r = ws.getRow(row)
    cols.forEach((c, i) => {
      const cell = r.getCell(i + 1)
      cell.value = (colToValue[c.key]?.(dataRow) ?? '') as ExcelJS.CellValue
      cell.font = font(10)
      cell.border = border
      cell.alignment =
        c.key === 'name' || c.key === 'article' ? { vertical: 'middle', wrapText: true } : center
      // Цена — полная (до 4 знаков): УНФ считает сумму строки от цены из
      // файла, и 12,35 вместо 12,3456 × 1000 шт дало бы 12 350,00 вместо
      // 12 345,60 из счёта CRM. Формат показывает 2–4 знака.
      if (c.key === 'price') cell.numFmt = '#,##0.00##'
    })
    row++
  }

  // ── Скачивание ──
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const safeClient = payload.clientName.replace(/[/\\:*?"<>|]/g, '').trim()
  const numPart = payload.requestNumber ? ` ${payload.requestNumber}` : ''
  // invoiceId === null → сводный шаблон по всем счетам заказа.
  const prefix = payload.invoiceId === null ? 'Сводный счёт' : 'Счёт'
  a.download = `${prefix}${numPart} ${safeClient}.xlsx`.trim()
  a.click()
  URL.revokeObjectURL(url)
}
